import { useRef, useImperativeHandle, forwardRef, useEffect, useState } from "react";

// Simple, dependency-free signature pad that works with mouse + touch
const SignaturePad = forwardRef(function SignaturePad({ onChange }, ref) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);

  useImperativeHandle(ref, () => ({
    clear: () => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      ctx.clearRect(0, 0, c.width, c.height);
      setIsEmpty(true);
      onChange?.(null);
    },
    isEmpty: () => isEmpty,
    toDataURL: () => {
      const c = canvasRef.current;
      if (!c || isEmpty) return null;
      return c.toDataURL("image/png");
    },
  }));

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;

    function resize() {
      const rect = c.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      c.width = rect.width * dpr;
      c.height = rect.height * dpr;
      const ctx = c.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2.2;
      ctx.strokeStyle = "#C9963F";
    }
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  function getPos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  }

  function start(e) {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = getPos(e);
  }

  function move(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (isEmpty) setIsEmpty(false);
  }

  function end(e) {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const c = canvasRef.current;
    onChange?.(c.toDataURL("image/png"));
  }

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        className="sig-canvas"
        data-testid="signature-canvas"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      {isEmpty && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-white/30 text-sm tracking-widest uppercase">
          Sign here
        </div>
      )}
    </div>
  );
});

export default SignaturePad;
